import { useEffect, useRef } from "react";
import { MicrophoneMenu } from "@/components/microphone-menu";
import {
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
} from "lucide-react";
import { ConversationPortrait } from "@/components/conversation-portrait";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  COACH_FALLBACK_LABEL,
  initialsOf,
  type ChatMode,
  type CoachStyle,
  type GespraechsPhase,
  type Persona,
} from "@nl/shared";
import {
  useVoiceSession,
  type VoiceStage,
  type VoiceStatus,
  type VoiceTurn,
} from "@/lib/realtime/use-voice-session";

/**
 * Who is on the line, and where the call is reported afterwards. The persona
 * variant belongs to a training session, the coach variant to the open chat,
 * which has neither session nor persona.
 */
export type VoiceTarget =
  | {
      kind: "persona";
      /** The training conversation this voice session belongs to. */
      sessionId: string;
      persona: Persona;
      /** "coach" keeps the session but puts the coach on the line. */
      mode?: "roleplay" | "coach";
      coach?: CoachStyle | null;
      /**
       * On the phone the advisor does not see her counterpart. Without this the
       * customer would sit in front of her in full portrait during the
       * appointment call she is only just placing — and the picture would take
       * the phone training's difficulty away.
       */
      amTelefon?: boolean;
      /**
       * Sparring instead of debriefing a roleplay, and which one.
       *
       * Ändert nur die Ansprache auf diesem Schirm; woran der Coach arbeitet,
       * entscheidet der Server anhand der Einheit der Sitzung.
       */
      sparringArt?: "vorbereitung" | "nachbereitung";
    }
  | {
      kind: "coach-thread";
      threadId?: string;
      coach: CoachStyle | null;
      chatMode?: ChatMode;
      trainingPhase?: GespraechsPhase;
      produkt?: string;
    }
  | {
      /** The coach about a whole Durchlauf, called from below the debrief. */
      kind: "run";
      runId: string;
      coach: CoachStyle | null;
    };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: VoiceTarget;
  onTranscriptComplete?: (turns: VoiceTurn[]) => void;
};


/**
 * Everything the UI and the connection need, derived from the target once.
 * The coach has a face since the coaches became people, so the avatar no
 * longer falls back to an icon just because it is not a persona.
 */
function describe(target: VoiceTarget) {
  if (target.kind === "run") {
    const name = target.coach?.name ?? COACH_FALLBACK_LABEL;
    return {
      title: name,
      subtitle: target.coach?.headline || COACH_FALLBACK_LABEL,
      portraitUrl: target.coach?.portraitUrl,
      initials: target.coach ? initialsOf(target.coach.name) : "",
      isCoach: true,
      hint: `Sprich mit ${name} über deinen ganzen Durchlauf: alle Einheiten, Feedbacks und den roten Faden dazwischen. Das Transkript landet danach im Gespräch.`,
      start: { target: "run" as const, runId: target.runId },
    };
  }

  if (target.kind === "coach-thread") {
    const name = target.coach?.name ?? COACH_FALLBACK_LABEL;
    return {
      title: name,
      subtitle: target.coach?.headline || COACH_FALLBACK_LABEL,
      portraitUrl: target.coach?.portraitUrl,
      initials: target.coach ? initialsOf(target.coach.name) : "",
      isCoach: true,
      hint:
        target.chatMode === "training"
          ? `${name} challenged dich sprachlich und erklärt, wo es hakt. Das Transkript landet danach im Gespräch.`
          : `Sprich frei mit ${name}. Das Transkript landet danach im Gespräch.`,
      start: {
        target: "coach-thread" as const,
        threadId: target.threadId,
        chatMode: target.chatMode,
        trainingPhase: target.trainingPhase,
        produkt: target.produkt,
      },
    };
  }

  const isCoach = target.mode === "coach";
  const name = isCoach
    ? (target.coach?.name ?? COACH_FALLBACK_LABEL)
    : target.persona.name;
  const sparring = isCoach ? target.sparringArt : undefined;
  return {
    title: name,
    subtitle: isCoach
      ? sparring === "vorbereitung"
        ? `Vorbereitung auf den Termin mit ${target.persona.name}`
        : sparring === "nachbereitung"
          ? `Nacharbeit zum Termin mit ${target.persona.name}`
          : `Feedback-Gespräch zu deiner Session mit ${target.persona.name}`
      : target.persona.profession,
    portraitUrl: isCoach
      ? target.coach?.portraitUrl
      : target.amTelefon
        ? undefined
        : target.persona.portraitUrl,
    initials: initialsOf(name),
    isCoach,
    hint: isCoach
      ? sparring === "vorbereitung"
        ? `Ihr geht die Leitfragen durch: ${name} kennt die Stammdaten und fragt nach, was du daraus ableitest. Was du festlegst, trägst du daneben in den Vorbereitungsbogen ein. Das Transkript landet danach im Gespräch.`
        : sparring === "nachbereitung"
          ? `Ihr geht die Leitfragen zur Nacharbeit durch: ${name} fragt nach, was verbindlich vereinbart ist und was offen blieb. Was ihr klärt, trägst du daneben in den Nachbereitungsbogen ein. Bewertet wird hier nichts — die Auswertung kommt danach.`
          : `Stell ${name} Fragen zum Feedback. Dein Coach kennt dein Gespräch und erklärt die Punkte sprachlich.`
      : target.amTelefon
        ? "Du rufst an. Dein KI-Kunde hört zu und antwortet live. Sehen kannst du ihn dabei nicht. Das Transkript wird beim Beenden in den Chat übernommen."
        : "Sprich frei. Dein KI-Kunde hört zu und antwortet live. Das Transkript wird beim Beenden in den Chat übernommen.",
    start: {
      sessionId: target.sessionId,
      voice: isCoach ? undefined : target.persona.voice,
      mode: target.mode ?? ("roleplay" as const),
    },
  };
}

/**
 * The live voice session shown *inline* inside the chat window (no dialog).
 * Owns its own connection: it starts on mount and reports its transcript via
 * `onEnd` when the user hangs up. The surrounding chat is expected to disable
 * its input while this is mounted.
 */
export function VoiceSessionPanel({
  target,
  onEnd,
  className,
}: {
  target: VoiceTarget;
  onEnd: (turns: VoiceTurn[]) => void;
  className?: string;
}) {
  const who = describe(target);
  const {
    status,
    stage,
    error,
    muted,
    remoteStream,
    localStream,
    start,
    stop,
    toggleMute,
    switchMicrophone,
    microphoneLabel,
  } = useVoiceSession();

  // Start once, as soon as the panel appears.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start(who.start);
    // Dialling once on mount; `who` is derived per render and must not retrigger.
  }, [start]);

  return (
    <VoiceSessionView
      who={who}
      status={status}
      stage={stage}
      error={error}
      muted={muted}
      remoteStream={remoteStream}
      localStream={localStream}
      onToggleMute={toggleMute}
      onSelectMicrophone={switchMicrophone}
      microphoneLabel={microphoneLabel}
      onEnd={async () => onEnd(await stop())}
      variant="inline"
      className={className}
    />
  );
}

export function VoiceSessionOverlay({
  open,
  onOpenChange,
  target,
  onTranscriptComplete,
}: Props) {
  const who = describe(target);
  const {
    status,
    stage,
    error,
    muted,
    remoteStream,
    localStream,
    start,
    stop,
    toggleMute,
    switchMicrophone,
    microphoneLabel,
  } = useVoiceSession();

  useEffect(() => {
    // For coach mode the backend loads transcript and feedback from the
    // session itself, so neither has to be sent along.
    if (open && status === "idle") void start(who.start);
  }, [open, status, start]);

  const finish = async () => {
    const collected = await stop();
    if (collected.length > 0) onTranscriptComplete?.(collected);
  };

  const handleEnd = async () => {
    await finish();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) void finish();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false} className="max-h-[90dvh] gap-0 overflow-y-auto p-0 sm:max-w-lg">
        <DialogTitle className="sr-only">{who.title}</DialogTitle>
        <DialogDescription className="sr-only">{who.subtitle}</DialogDescription>
        <VoiceSessionView
          who={who}
          status={status}
          stage={stage}
          error={error}
          muted={muted}
          remoteStream={remoteStream}
          localStream={localStream}
          onToggleMute={toggleMute}
          onSelectMicrophone={switchMicrophone}
          microphoneLabel={microphoneLabel}
          onEnd={handleEnd}
          variant="dialog"
        />
      </DialogContent>
    </Dialog>
  );
}

/** Presentational voice-session UI, shared by the dialog and the inline panel. */
function VoiceSessionView({
  who,
  status,
  stage,
  error,
  muted,
  remoteStream,
  localStream,
  onToggleMute,
  onSelectMicrophone,
  microphoneLabel,
  onEnd,
  variant,
  className,
}: {
  who: ReturnType<typeof describe>;
  status: VoiceStatus;
  stage: VoiceStage;
  error: string | null;
  muted: boolean;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
  onToggleMute: () => void;
  onSelectMicrophone: (deviceId: string | null) => Promise<void>;
  microphoneLabel: string;
  onEnd: () => void;
  variant: "dialog" | "inline";
  className?: string;
}) {
  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="relative shrink-0 overflow-hidden rounded-t-xl">
        <ConversationPortrait portraitUrl={who.portraitUrl} name={who.title} className="h-[clamp(160px,30dvh,320px)] w-full" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-5 pb-5 pt-16 text-left text-white">
          <div className="font-display text-2xl font-medium tracking-tight">{who.title}</div>
          <p className="mt-1 text-[14px] leading-snug text-white/90">{who.subtitle}</p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3 px-5 py-4 text-center">
        <StatusPill status={status} stage={stage} error={error} />

        <AudioVisualizer
          remoteStream={remoteStream}
          localStream={muted ? null : localStream}
          active={status === "live"}
        />

        {error && <p className="text-[13.5px] text-destructive">{error}</p>}

        <p className="max-w-xs text-[13.5px] text-muted-foreground">{who.hint}</p>
      </div>

      <div
        className={cn(
          "sticky bottom-0 flex items-center justify-center gap-3 border-t bg-card p-4",
          variant === "dialog" && "rounded-b-xl",
        )}
      >
        <div className="flex items-center gap-0.5" role="group" aria-label="Mikrofon">
          <Button
            variant={muted ? "secondary" : "outline"}
            size="icon"
            onClick={onToggleMute}
            disabled={status !== "live"}
            aria-label={muted ? "Stummschaltung aufheben" : "Stummschalten"}
            className="h-10 w-10 rounded-full"
          >
            {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          <MicrophoneMenu
            onSelect={onSelectMicrophone}
            activeLabel={microphoneLabel}
            disabled={status !== "live"}
            className="h-10 rounded-full"
          />
        </div>
        <Button
          variant="destructive"
          onClick={onEnd}
          disabled={status === "ending"}
          className="gap-2 rounded-full px-5"
        >
          <PhoneOff className="h-4 w-4" />
          {status === "ending" ? "Beende…" : "Beenden"}
        </Button>
      </div>
    </div>
  );
}

const BAR_COUNT = 36;

function AudioVisualizer({
  remoteStream,
  localStream,
  active,
}: {
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
  active: boolean;
}) {
  const barsRef = useRef<Array<HTMLSpanElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const analysersRef = useRef<AnalyserNode[]>([]);
  const sourcesRef = useRef<MediaStreamAudioSourceNode[]>([]);

  const anyStream = remoteStream || localStream;

  // The microphone stream already exists while the call is still connecting;
  // analysing it then would let the bars react to the room before anyone is on
  // the line.
  useEffect(() => {
    if (!anyStream || !active) return;

    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const ctx = new AC();
    ctxRef.current = ctx;

    const buildAnalyser = (s: MediaStream) => {
      const source = ctx.createMediaStreamSource(s);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      sourcesRef.current.push(source);
      analysersRef.current.push(analyser);
      return analyser;
    };

    const analysers: AnalyserNode[] = [];
    if (remoteStream) analysers.push(buildAnalyser(remoteStream));
    if (localStream) analysers.push(buildAnalyser(localStream));

    const binCount = analysers[0].frequencyBinCount;
    const freq = new Uint8Array(binCount);
    const peak = new Uint8Array(binCount);

    const tick = () => {
      peak.fill(0);
      for (const a of analysers) {
        a.getByteFrequencyData(freq);
        for (let i = 0; i < binCount; i++) {
          if (freq[i] > peak[i]) peak[i] = freq[i];
        }
      }
      const usable = Math.floor(binCount * 0.66);
      for (let i = 0; i < BAR_COUNT; i++) {
        const idx = Math.floor((i / BAR_COUNT) * usable);
        const v = peak[idx] / 255;
        const mid = 1 - Math.abs(i - BAR_COUNT / 2) / (BAR_COUNT / 2);
        const h = Math.max(3, Math.round(4 + v * 36 * (0.55 + 0.45 * mid)));
        const el = barsRef.current[i];
        if (el) {
          el.style.height = `${h}px`;
          el.style.opacity = `${0.45 + v * 0.55}`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try {
        sourcesRef.current.forEach((s) => s.disconnect());
        analysersRef.current.forEach((a) => a.disconnect());
        ctxRef.current?.close();
      } catch {
        // ignore
      }
      sourcesRef.current = [];
      analysersRef.current = [];
      ctxRef.current = null;
    };
  }, [remoteStream, localStream, anyStream, active]);

  // Without a stream the bars are simulated. That only makes sense once the
  // call is up: a waveform moving while the pill still says "Verbinde…" claims
  // a connection that does not exist yet. Before that the bars stand still.
  useEffect(() => {
    if (!active) {
      for (const el of barsRef.current) {
        if (el) {
          el.style.height = "4px";
          el.style.opacity = "0.35";
        }
      }
      return;
    }
    if (anyStream) return;

    let raf = 0;
    const start = performance.now();
    const loop = (t: number) => {
      const elapsed = (t - start) / 1000;
      for (let i = 0; i < BAR_COUNT; i++) {
        const phase = elapsed * 2 + i * 0.35;
        const v = (Math.sin(phase) + 1) / 2;
        const h = Math.max(3, Math.round(4 + v * 14));
        const el = barsRef.current[i];
        if (el) {
          el.style.height = `${h}px`;
          el.style.opacity = "0.7";
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [anyStream, active]);

  return (
    <div
      className="flex h-12 items-center justify-center gap-[3px]"
      aria-hidden
    >
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <span
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          className={cn(
            "w-[3px] rounded-full bg-primary transition-[height,opacity] duration-75 ease-out",
            !active && "bg-muted-foreground/60"
          )}
          style={{ height: "4px" }}
        />
      ))}
    </div>
  );
}

function StatusPill({
  status,
  stage,
  error,
}: {
  status: ReturnType<typeof useVoiceSession>["status"];
  stage: ReturnType<typeof useVoiceSession>["stage"];
  error: string | null;
}) {
  let label = "Bereit";
  let tone = "text-muted-foreground";
  let dot = "bg-muted-foreground/60";

  if (status === "connecting") {
    // Naming the microphone step matters: an unanswered permission dialog is
    // one of the ways a call never starts, and "Verbinde…" hid it completely.
    label = stage === "mic" ? "Warte auf Mikrofon…" : "Verbinde…";
    tone = "text-muted-foreground";
    dot = "bg-amber-500";
  } else if (status === "live") {
    label = "Live";
    tone = "text-primary";
    dot = "bg-primary";
  } else if (status === "ending") {
    label = "Beende…";
  } else if (status === "error" || error) {
    label = "Fehler";
    tone = "text-destructive";
    dot = "bg-destructive";
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 font-bold text-[11.5px] tracking-[0.06em] uppercase",
        tone
      )}
    >
      {status === "connecting" ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <span className="relative flex h-2 w-2">
          {status === "live" && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
          )}
          <span className={cn("relative inline-flex h-2 w-2 rounded-full", dot)} />
        </span>
      )}
      {label}
    </div>
  );
}
